package ai.jessie.web.work

import ai.jessie.shared.work.WorkCalendarResponse
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

/**
 * Step 6, BR-047 and BR-050: the week-view period as labelled controls, usable by keyboard and at
 * any width. The day is one of the server's dates, so a period cannot leave its date.
 */
@Composable
internal fun calendarSelectionForm(
    calendar: WorkCalendarResponse,
    state: WorkCalendarUiState,
    actions: WorkCalendarActions,
) {
    val dates = calendar.creatableDates()
    if (dates.isEmpty()) {
        P { Text("Arbeit kann nur bis heute erfasst werden; diese Woche liegt in der Zukunft.") }
        return
    }
    Form(
        attrs = {
            // The step only guides the picker; an unaligned time is snapped, not refused.
            attr("novalidate", "")
            addEventListener("submit") {
                it.preventDefault()
                actions.createFromSelection()
            }
        }
    ) {
        Fieldset(
            attrs =
                FieldsetStyle.toAttrs {
                    if (state.busy) attr("disabled", "")
                }
        ) {
            Legend { Text("Arbeit aus einem leeren Zeitraum erfassen") }
            P(attrs = { id(SELECTION_HINT_ID) }) {
                Text(
                    "Beginn und Ende liegen am gewählten Tag und werden auf $SNAP_MINUTES Minuten " +
                        "gerundet. Gespeichert werden das Datum und die Dauer, nicht die Uhrzeit."
                )
            }
            selectionDay(dates, state, actions)
            selectionTime("calendar-from", "Beginn", state.selection.from, state) { value ->
                actions.select { it.copy(from = value) }
            }
            selectionTime("calendar-to", "Ende", state.selection.to, state) { value ->
                actions.select { it.copy(to = value) }
            }
            // A4: the requirement is explained next to the controls it concerns.
            state.selectionProblem?.let {
                P(
                    attrs = {
                        id(SELECTION_PROBLEM_ID)
                        attr("role", "alert")
                    }
                ) {
                    Text(it)
                }
            }
            themedButton("Zeitraum übernehmen", {}, submit = true)
        }
    }
}

@Composable
private fun selectionDay(
    dates: List<String>,
    state: WorkCalendarUiState,
    actions: WorkCalendarActions,
) {
    P(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId("calendar-date") }) { Text("Tag") }
        Select(
            attrs =
                FieldInputStyle.toAttrs {
                    id("calendar-date")
                    selectionAttributes(state)
                    onChange { event -> actions.select { it.copy(date = event.value.orEmpty()) } }
                }
        ) {
            Option("", attrs = { if (state.selection.date.isEmpty()) selected() }) {
                Text("Bitte wählen")
            }
            dates.forEach { date ->
                Option(date, attrs = { if (date == state.selection.date) selected() }) {
                    Text("${weekdayLabel(date)}, $date")
                }
            }
        }
    }
}

@Composable
private fun selectionTime(
    id: String,
    label: String,
    value: String,
    state: WorkCalendarUiState,
    change: (String) -> Unit,
) {
    P(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(id) }) { Text(label) }
        Input(
            InputType.Time,
            attrs =
                FieldInputStyle.toAttrs {
                    id(id)
                    value(value)
                    attr("step", "${SNAP_MINUTES * SECONDS_PER_MINUTE}")
                    selectionAttributes(state)
                    onInput { change(it.value) }
                },
        )
    }
}

private fun AttrsScope<*>.selectionAttributes(state: WorkCalendarUiState) {
    attr("autocomplete", "off")
    attr(
        "aria-describedby",
        if (state.selectionProblem == null) SELECTION_HINT_ID else SELECTION_PROBLEM_ID,
    )
    if (state.selectionProblem != null) attr("aria-invalid", "true")
}

private const val SELECTION_HINT_ID = "calendar-selection-hint"
private const val SELECTION_PROBLEM_ID = "calendar-selection-problem"
private const val SECONDS_PER_MINUTE = 60
